import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { utils } from '@app/shared';
import { Task, Ticket, TicketTask, TicketType } from 'src/app/models';
import { TcDataService, TcTicketService } from 'src/app/shared/services';
import { PatronColors } from '@teorema/patron';

@Component({
  selector: 'app-ticket-card-container',
  templateUrl: './ticket-card-container.component.html',
  styleUrls: ['./ticket-card-container.component.scss'],
  standalone: false,
})
export class TicketCardContainerComponent implements OnInit {
  @Input() ticketTask!: TicketTask;
  @Input() user!: string;
  @Input() title?: string;
  @Input() color?: string;
  @Input() showDate;
  @Input() readOnly: boolean = false;
  //SprintBeta
  @Input() isBeta: boolean = false;

  @Output() click = new EventEmitter<any>();

  totalPoints?: number;
  completedPoints?: number;
  progressPercentage?: number;

  constructor(
    private tcDataService: TcDataService,
    public tcTicketService: TcTicketService
  ) {}

  ngOnInit(): void {
    this.setPoints();
  }

  get ticket(): Ticket | undefined {
    return this.ticketTask?.ticket;
  }

  get task(): Task | undefined {
    return this.ticketTask?.task;
  }

  get ticket_type_description(): string | undefined {
    return (this.ticket?.ticket_type as TicketType)?.description;
  }

  get ticket_developer(): string | undefined {
    return this.ticket?.ticketUsers?.find(tu => tu.user_type === 'D')?.user?.name;
  }

  get tasksByDev(): Task[] | any | undefined {
    return this.removeTasksOutsideSprint(
      this.ticket?.tasks?.filter(task => task?.user?.name === this.user)
    );
  }

  get difficultyBadgeColor(): PatronColors | undefined {
    return this.tcTicketService.getDifficultyBadgeColor(this.task?.difficulty) as PatronColors;
  }

  get difficultyLabel(): string | undefined {
    return this.tcTicketService.getDifficultyLabel(this.task?.difficulty);
  }

  difficultyBadgeColorByValue(value): PatronColors | undefined {
    return this.tcTicketService.getDifficultyBadgeColor(value) as PatronColors;
  }

  difficultyLabelByValue(value): string | undefined {
    return this.tcTicketService.getDifficultyLabel(value);
  }

  getStatusClass(status): PatronColors | undefined {
    return this.tcDataService.getTicketStatusClass(status) as PatronColors;
  }

  setPoints() {
    if (this.ticket?.tasks) {
      let tasks = this.ticket.tasks.filter(t => t.estimated_effort);

      this.totalPoints = tasks
        .map(t => Number(t.estimated_effort))
        .reduce((acc, effort) => {
          return acc + effort;
        }, 0);

      this.completedPoints = tasks
        .filter(t => t.finished)
        .map(t => Number(t.estimated_effort))
        .reduce((acc, effort) => {
          return acc + effort;
        }, 0);

      if (this.totalPoints && this.completedPoints) {
        this.progressPercentage = Math.floor((this.completedPoints / this.totalPoints) * 100);
      }
    }
  }

  get userLog() {
    if (this.task?.userLog) {
      return this.task?.userLog;
    } else {
      let taskWithUserLog: any = null;

      if (this.ticket?.tasks) {
        taskWithUserLog = this.ticket.tasks.find(t => t.userLog);
      }

      if (taskWithUserLog) {
        //Outra tarefa desse chamado está sendo apontada
        //Portanto, não mostrar apontamento nessa tarefa,
        //para mostrar apenas na que está sendo apontada
      } else {
        return this.ticket?.userLog;
      }
    }

    return '';
  }

  // get systems() {
  //   if (this.ticketTask?.task?.systems?.length) {
  //     return this.ticketTask?.task?.systems;
  //   } else {
  //     return [ this.ticketTask?.ticket?.system ];
  //   }
  // }

  get system() {
    if (this.ticketTask?.task?.systems?.length) {
      return this.ticketTask?.task?.systems[0];
    } else {
      return this.ticketTask?.ticket?.system;
    }
  }

  removeTasksOutsideSprint(list): any {
    return list?.filter(item => {
      // Logic for 'Atrasados'
      if (this?.title?.includes('Atrasados')) {
        const titleNormalized = this?.title?.replace('Atrasados', '');
        const [day, month, year] = titleNormalized.split('/').map(Number);
        const data = new Date(year, month - 1, day);
        let isDelayed = !item.finished && this.isSameDate(item.expected_date, data?.toString()!);

        if (isDelayed) {
          return true;
        }
        return false;
      }

      // Logic for 'Não pontuados' (No Date + No Effort + Not Finished)
      if (this?.title === 'Não pontuados') {
        const hasDate = item.expected_date;
        return !hasDate && !item.estimated_effort && !item.finished;
      }

      // Logic for 'Já pontuados' (No Date + Effort + Not Finished)
      if (this?.title === 'Já pontuados') {
        const hasDate = item.expected_date;
        return !hasDate && item.estimated_effort && !item.finished;
      }

      const isExpectedDate = this.isSameDate(item.expected_date, this?.title!);
      const isFinished = this?.title === 'Concluídos' && item.finished;

      if (isExpectedDate || isFinished) {
        return true;
      }
      return false;
    });
  }

  isSameDate(date1: string | null, date2: string): boolean {
    if (!date1) return false;

    const d1 = new Date(date1);
    const date2Fixed = /^\d{4}-\d{2}-\d{2}$/.test(date2) ? `${date2}T12:00:00` : date2;

    const d2 = new Date(date2Fixed);

    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  isDevCanRead() {
    return utils.checkAccess(['Development'], true).result;
  }

  getColorTaskCard() {
    return this.color
      ? this.tcTicketService.getColor(this.color, 'alert-') + ' ticket-card-shadow'
      : 'alert-ticket-card';
  }
}
